import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Tag,
  Select,
  message,
  Row,
  Col,
  Divider,
  Typography
} from 'antd';
import { SaveOutlined, EyeOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import MDEditor from '@uiw/react-md-editor';
import { mockPosts } from '../../api/mockData';
import './index.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const WritePost = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  // 预定义标签
  const presetTags = [
    'React', 'Vue', 'JavaScript', 'TypeScript', 'Node.js',
    'Python', 'Java', 'CSS', 'HTML', 'Docker',
    '机器学习', '人工智能', '前端', '后端', '数据库'
  ];

  // 如果是编辑模式，初始化数据
  React.useEffect(() => {
    if (isEditMode) {
      const post = mockPosts.find(p => p.id === parseInt(id));
      if (post) {
        form.setFieldsValue({
          title: post.title,
          summary: post.summary,
          category: post.category
        });
        setContent(post.content);
        setTags(post.tags);
      }
    }
  }, [id, isEditMode, form]);

  const handleTagClose = (removedTag) => {
    const newTags = tags.filter(tag => tag !== removedTag);
    setTags(newTags);
  };

  const handleTagInputConfirm = () => {
    if (inputValue && tags.indexOf(inputValue) === -1) {
      setTags([...tags, inputValue]);
    }
    setInputValue('');
  };

  const handleTagSelect = (tag) => {
    if (tags.indexOf(tag) === -1) {
      setTags([...tags, tag]);
    }
  };

  const handleSubmit = async (values) => {
    if (!content.trim()) {
      message.error('请填写文章内容');
      return;
    }

    if (tags.length === 0) {
      message.error('请至少添加一个标签');
      return;
    }

    setLoading(true);
    try {
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const postData = {
        ...values,
        content,
        tags,
        id: isEditMode ? parseInt(id) : Date.now(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: '当前用户',
        viewCount: 0,
        likeCount: 0,
        commentCount: 0,
        coverImage: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=400&fit=crop'
      };

      console.log('保存文章:', postData);
      message.success(isEditMode ? '文章更新成功！' : '文章发布成功！');
      
      // 跳转到文章详情页
      navigate(`/post/${postData.id}`);
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    const values = form.getFieldsValue();
    if (!values.title) {
      message.warning('请先填写标题');
      return;
    }
    // 在实际应用中，这里可以打开预览模态框
    message.info('预览功能开发中');
  };

  return (
    <div className="write-post-page">
      <div className="write-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="back-btn"
        >
          返回
        </Button>
        <Title level={3} className="page-title">
          {isEditMode ? '编辑文章' : '撰写新文章'}
        </Title>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="post-form"
      >
        <Row gutter={[32, 32]}>
          <Col xs={24} lg={16}>
            {/* 文章标题 */}
            <Card className="form-section" title="文章标题">
              <Form.Item
                name="title"
                rules={[
                  { required: true, message: '请输入文章标题' },
                  { max: 100, message: '标题不能超过100个字符' }
                ]}
              >
                <Input
                  placeholder="请输入文章标题"
                  size="large"
                  className="title-input"
                />
              </Form.Item>
            </Card>

            {/* 文章摘要 */}
            <Card className="form-section" title="文章摘要">
              <Form.Item
                name="summary"
                rules={[
                  { required: true, message: '请输入文章摘要' },
                  { max: 200, message: '摘要不能超过200字' }
                ]}
              >
                <TextArea
                  placeholder="请输入文章摘要，不超过200字"
                  rows={4}
                  showCount
                  maxLength={200}
                  className="summary-input"
                />
              </Form.Item>
            </Card>

            {/* 文章内容 */}
            <Card className="form-section" title="文章内容（支持Markdown语法）">
              <div className="editor-container">
                <MDEditor
                  value={content}
                  onChange={setContent}
                  height={400}
                  preview="edit"
                  className="md-editor"
                />
                <div className="editor-tips">
                  <Text type="secondary">
                    提示：使用Markdown语法编写，支持标题、列表、代码块、图片等格式
                  </Text>
                </div>
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            {/* 标签 */}
            <Card className="form-section" title="标签">
              <div className="tags-section">
                <div className="selected-tags">
                  <Text>已选标签：</Text>
                  {tags.map(tag => (
                    <Tag
                      key={tag}
                      closable
                      onClose={() => handleTagClose(tag)}
                      className="post-tag"
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>

                <Divider />

                <div className="tag-input-section">
                  <Space.Compact style={{ width: '100%' }}>
                    <Input
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onPressEnter={handleTagInputConfirm}
                      placeholder="输入标签，按回车添加"
                      className="tag-input"
                    />
                    <Button type="primary" onClick={handleTagInputConfirm}>
                      添加
                    </Button>
                  </Space.Compact>
                </div>

                <div className="preset-tags">
                  <Text type="secondary" className="preset-title">
                    常用标签：
                  </Text>
                  <div className="tag-list">
                    {presetTags.map(tag => (
                      <Tag
                        key={tag}
                        color="blue"
                        onClick={() => handleTagSelect(tag)}
                        className="preset-tag"
                      >
                        {tag}
                      </Tag>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* 分类 */}
            <Card className="form-section" title="分类">
              <Form.Item
                name="category"
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Select placeholder="请选择文章分类" className="category-select">
                  <Option value="前端开发">前端开发</Option>
                  <Option value="后端技术">后端技术</Option>
                  <Option value="人工智能">人工智能</Option>
                  <Option value="数据库">数据库</Option>
                  <Option value="运维部署">运维部署</Option>
                  <Option value="生活随笔">生活随笔</Option>
                  <Option value="读书笔记">读书笔记</Option>
                </Select>
              </Form.Item>
            </Card>

            {/* 操作按钮 */}
            <Card className="form-section">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  block
                  size="large"
                >
                  {isEditMode ? '更新文章' : '发布文章'}
                </Button>

                <Button
                  type="default"
                  icon={<EyeOutlined />}
                  onClick={handlePreview}
                  block
                  size="large"
                >
                  预览文章
                </Button>

                <Button
                  type="text"
                  onClick={() => {
                    form.resetFields();
                    setContent('');
                    setTags([]);
                  }}
                  block
                >
                  重置表单
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default WritePost;